import { datadogRum } from '@datadog/browser-rum';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
  getPlanAndAssignmentsQueryKey,
  unassignUsers,
} from '@cfa/pathway-fe-shared/xp';

import { invalidateCertificateQueries } from '@/utils/queryInvalidation';

import ConfirmationModal from '@/components/popups/ConfirmationModal';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import { queryClient } from '@/services';

interface UnassignTeamMemberProps {
  activeTeamMember: { id: string; name: string };
  isOpen: boolean;
  onClose: () => void;
  planId: string;
}

const UnassignTeamMember: React.FC<UnassignTeamMemberProps> = ({
  activeTeamMember,
  isOpen,
  onClose,
  planId,
}) => {
  const { t } = useTranslation();
  const onUnassignTeamMember = () => {
    const userIds = [activeTeamMember.id];

    unassignUsers({ path: { planId }, body: { userIds } })
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: getPlanAndAssignmentsQueryKey({
            path: { planId },
          }),
        });
        invalidateCertificateQueries(planId, userIds);
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id}>
            {t('TrainingPlans.toastMessage.unassigned', {
              teamMember: activeTeamMember.name,
            })}
          </ToastMessageBlock>
        ));
      })
      .catch((err: unknown) => {
        datadogRum.addError(
          err instanceof Error ? err : new Error(String(err)),
        );
      });
    onClose();
  };

  return (
    <ConfirmationModal
      bodyText={t('TrainingPlans.teamMembers.unassignConfirmation', {
        name: activeTeamMember.name,
      })}
      headerText={t('Generic.unassign')}
      isOpen={isOpen}
      onClose={onClose}
      primaryButtonColor="primary"
      primaryButtonHandler={onUnassignTeamMember}
      primaryButtonText={t('Button.unassign')}
      secondaryButtonHandler={onClose}
      secondaryButtonText={t('Button.cancel')}
    />
  );
};

export default UnassignTeamMember;
